import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PackageIcon, EyeIcon, UsersIcon, TrendingUpIcon, ArrowRightIcon, PlusIcon, BotIcon, LayersIcon, CarIcon } from 'lucide-react';
import { useBusiness, getHubTabs } from './BusinessContext';
interface BusinessDashboardProps {
  onNavigate?: (tab: string) => void;
}
export function BusinessDashboard({
  onNavigate
}: BusinessDashboardProps) {
  const {
    hub
  } = useBusiness();
  const tabs = getHubTabs(hub?.type ?? 'generic');
  const inventoryLabel = tabs[1];
  const businessName = hub?.name || 'there';
  // Mock Data organized around user categories
  const categories = [{
    name: 'SUVs',
    listings: 42,
    views: '12.4k',
    leads: 156,
    trend: '+14%'
  }, {
    name: 'Sedans',
    listings: 28,
    views: '8.2k',
    leads: 89,
    trend: '+5%'
  }, {
    name: 'Hatchbacks',
    listings: 15,
    views: '3.1k',
    leads: 24,
    trend: '-2%'
  }];
  const kpis = [{
    label: `Total ${inventoryLabel}`,
    value: '85',
    trend: '+4'
  }, {
    label: 'Total Views',
    value: '23.7k',
    trend: '+12%'
  }, {
    label: 'New Leads',
    value: '269',
    trend: '+8%'
  }, {
    label: 'Conversion',
    value: '3.2%',
    trend: '+0.4%'
  }];
  const topPerforming = [{
    id: 1,
    title: 'Toyota Prado GXR 2023',
    category: 'SUVs',
    views: '1.2k',
    leads: 45,
    img: 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=200'
  }, {
    id: 2,
    title: 'Nissan Patrol V8 2022',
    category: 'SUVs',
    views: '980',
    leads: 38,
    img: 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&q=80&w=200'
  }, {
    id: 3,
    title: 'Honda Accord Sport 2023',
    category: 'Sedans',
    views: '850',
    leads: 29,
    img: 'https://images.unsplash.com/photo-1606152421802-db97b9c7a11b?auto=format&fit=crop&q=80&w=200'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  // Graceful Empty State
  if (categories.length === 0) {
    return <motion.div variants={containerVariants} initial="hidden" animate="show" className="max-w-3xl mx-auto text-center py-24 px-4" data-id="element-591">
        <motion.div variants={itemVariants} className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-slate-100 shadow-sm" data-id="element-592">
          <PackageIcon className="w-10 h-10 text-slate-300" data-id="element-593" />
        </motion.div>
        <motion.h2 variants={itemVariants} className="font-display font-bold text-3xl text-dark-navy mb-4" data-id="element-594">
          Welcome to your Business Hub
        </motion.h2>
        <motion.p variants={itemVariants} className="text-slate-500 text-lg mb-10 max-w-lg mx-auto leading-relaxed" data-id="element-595">
          Your hub is ready. Add your first {inventoryLabel.toLowerCase()} to
          start receiving leads and let Talab AI handle customer inquiries.
        </motion.p>
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-596">
          <button onClick={() => onNavigate?.(inventoryLabel)} className="w-full sm:w-auto px-6 py-3.5 bg-primary-indigo text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-597">
            <PlusIcon className="w-5 h-5" data-id="element-598" /> Add {inventoryLabel}
          </button>
          <button onClick={() => onNavigate?.('AI Channels')} className="w-full sm:w-auto px-6 py-3.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-599">
            <BotIcon className="w-5 h-5 text-slate-400" data-id="element-600" /> AI Channels
          </button>
        </motion.div>
      </motion.div>;
  }
  // Active Populated State
  return <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-6 pb-12" data-id="element-601">
      {/* KPI Strip */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 gap-3" data-id="element-602">
        {kpis.map(kpi => <div key={kpi.label} className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm flex flex-col justify-between" data-id="element-603">
            <p className="text-xs font-medium text-slate-500 mb-2" data-id="element-604">
              {kpi.label}
            </p>
            <div className="flex items-end gap-2" data-id="element-605">
              <p className="text-2xl font-display font-bold text-dark-navy leading-none tracking-tight" data-id="element-606">
                {kpi.value}
              </p>
              {kpi.trend && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mb-0.5 ${kpi.trend.startsWith('+') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-607">
                  {kpi.trend}
                </span>}
            </div>
          </div>)}
      </motion.div>

      {/* Categories Section */}
      <motion.div variants={itemVariants} data-id="element-608">
        <div className="flex items-center justify-between mb-3" data-id="element-609">
          <h3 className="font-display font-bold text-lg text-dark-navy flex items-center gap-1.5" data-id="element-610">
            <LayersIcon className="w-4 h-4 text-primary-indigo" data-id="element-611" /> Your
            Categories
          </h3>
          <button onClick={() => onNavigate?.(inventoryLabel)} className="text-[13px] font-semibold text-primary-indigo flex items-center gap-1" data-id="element-612">
            View all <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-613" />
          </button>
        </div>
        <div className="flex overflow-x-auto gap-3 pb-2 -mx-4 px-4 hide-scrollbar" data-id="element-614">
          {categories.map(cat => <button key={cat.name} onClick={() => onNavigate?.(inventoryLabel)} className="flex-shrink-0 w-64 text-left bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm active:scale-[0.98] transition-all flex flex-col" data-id="element-615">
              <div className="flex items-start justify-between mb-4" data-id="element-616">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center" data-id="element-617">
                  <CarIcon className="w-5 h-5 text-primary-indigo" data-id="element-618" />
                </div>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cat.trend.startsWith('+') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-619">
                  {cat.trend}
                </span>
              </div>
              <h4 className="font-bold text-base text-dark-navy mb-0.5" data-id="element-620">
                {cat.name}
              </h4>
              <p className="text-xs text-slate-500 mb-4" data-id="element-621">
                {cat.listings} active listings
              </p>

              <div className="mt-auto grid grid-cols-2 gap-3 pt-3 border-t border-slate-100" data-id="element-622">
                <div data-id="element-623">
                  <p className="text-[10px] font-medium text-slate-400 mb-0.5 flex items-center gap-1" data-id="element-624">
                    <EyeIcon className="w-3 h-3" data-id="element-625" /> Views
                  </p>
                  <p className="font-bold text-slate-700 text-sm" data-id="element-626">
                    {cat.views}
                  </p>
                </div>
                <div data-id="element-627">
                  <p className="text-[10px] font-medium text-slate-400 mb-0.5 flex items-center gap-1" data-id="element-628">
                    <UsersIcon className="w-3 h-3" data-id="element-629" /> Leads
                  </p>
                  <p className="font-bold text-slate-700 text-sm" data-id="element-630">
                    {cat.leads}
                  </p>
                </div>
              </div>
            </button>)}
        </div>
      </motion.div>

      {/* Top Performing */}
      <motion.div variants={itemVariants} data-id="element-631">
        <h3 className="font-display font-bold text-lg text-dark-navy mb-3 flex items-center gap-1.5" data-id="element-632">
          <TrendingUpIcon className="w-4 h-4 text-primary-indigo" data-id="element-633" /> Top
          Performing
        </h3>
        <div className="grid grid-cols-1 gap-3" data-id="element-634">
          {topPerforming.map(item => <button key={item.id} onClick={() => onNavigate?.(inventoryLabel)} className="text-left bg-white rounded-2xl p-3 border border-slate-200/60 shadow-sm flex gap-3 items-center active:bg-slate-50 transition-colors" data-id="element-635">
              <div className="w-16 h-16 bg-slate-100 rounded-xl flex-shrink-0 overflow-hidden" data-id="element-636">
                <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-637" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-638">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5 block" data-id="element-639">
                  {item.category}
                </span>
                <h4 className="font-bold text-sm text-dark-navy truncate mb-1.5" data-id="element-640">
                  {item.title}
                </h4>
                <div className="flex items-center gap-2 text-[11px] font-medium text-slate-500" data-id="element-641">
                  <span className="flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded" data-id="element-642">
                    <EyeIcon className="w-3 h-3" data-id="element-643" /> {item.views}
                  </span>
                  <span className="flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded" data-id="element-644">
                    <UsersIcon className="w-3 h-3" data-id="element-645" /> {item.leads}
                  </span>
                </div>
              </div>
            </button>)}
        </div>
      </motion.div>
    </motion.div>;
}